import { useState, useEffect, useCallback, useRef } from 'react';
import { GroupedVirtuosoHandle } from 'react-virtuoso';

interface KeyboardNavState {
    currentIndex?: number;
    isFocusingLeft?: boolean;
    isFocusingRight?: boolean;
}

interface UseTileKeyboardNavigationReturnType {
    currentIndex?: number;
    isFocusingLeft?: boolean;
    isFocusingRight?: boolean;
    clearKeyboardNavState: () => void;
    virtuosoRef: React.RefObject<GroupedVirtuosoHandle>;
}

type StoreType = {
    data: {
        list: any[];
    };
};

type GetItemTypeFromStore<L extends { data: { list: any[] } }> = L['data']['list'][number];

export function useKeyboardNavigation<T extends StoreType>({
    store,
    onSelectItem,
    onSetPrimaryItem,
    element,
    canFocusPrimary,
}: {
    store: T;
    onSelectItem: (item: GetItemTypeFromStore<T>) => void;
    onSetPrimaryItem: (item: GetItemTypeFromStore<T>) => void;
    /**
     * The `element` to which keyboard events should be bound to
     *
     * By default, we'll bind to the window.document.  However,
     * this has the side-effect of responding to all keyboard events,
     * no matter where the current focus is.
     *
     * By specifying an `element`, keyboard events will only be triggered
     * when they occur within the passed `element`
     */
    element?: HTMLElement | null;
    canFocusPrimary: (id: number) => boolean;
}): UseTileKeyboardNavigationReturnType {
    const [keyboardNavState, setKeyboardNavState] = useState<KeyboardNavState>({});
    const { currentIndex } = keyboardNavState;
    const virtuosoRef = useRef<GroupedVirtuosoHandle>(null);

    const elementToBindTo = element || window.document;

    const clearKeyboardNavState = useCallback(() => {
        setKeyboardNavState({ currentIndex: undefined });
    }, []);

    // Set focus to the the next focusable thing
    const focusNext = useCallback(
        ({
            ignoreFocusSide,
        }: {
            /**
             * Ignore whether or not we are focused on the left or right side,
             * and move directly to the next item in the list
             */
            ignoreFocusSide: boolean;
        }) => {
            const storeListLength = store.data.list.length;
            setKeyboardNavState(({ currentIndex, isFocusingRight }) => {
                if (typeof currentIndex === 'undefined') {
                    return {
                        currentIndex: 0,
                        isFocusingRight: ignoreFocusSide ? undefined : false,
                        isFocusingLeft: ignoreFocusSide ? undefined : true,
                    };
                } else {
                    const nextIndex = (currentIndex + 1) % storeListLength;
                    if (ignoreFocusSide) {
                        return {
                            currentIndex: nextIndex,
                            isFocusingRight: undefined,
                            isFocusingLeft: undefined,
                        };
                    } else {
                        if (isFocusingRight) {
                            return {
                                currentIndex: nextIndex,
                                isFocusingRight: false,
                                isFocusingLeft: true,
                            };
                        } else {
                            const currentId = store.data.list[currentIndex]?.id;
                            if (currentId && canFocusPrimary(currentId)) {
                                return {
                                    currentIndex,
                                    isFocusingRight: true,
                                    isFocusingLeft: false,
                                };
                            } else {
                                return {
                                    currentIndex: nextIndex,
                                    isFocusingRight: false,
                                    isFocusingLeft: true,
                                };
                            }
                        }
                    }
                }
            });
        },
        [canFocusPrimary, store]
    );

    // Set focus to the the previous focusable thing
    const focusPrev = useCallback(
        ({
            ignoreFocusSide,
        }: {
            /**
             * Ignore whether or not we are focused on the left or right side,
             * and move directly to the prev item in the list
             */
            ignoreFocusSide: boolean;
        }) => {
            const storeListLength = store.data.list.length;
            setKeyboardNavState(({ currentIndex, isFocusingLeft }) => {
                if (typeof currentIndex === 'undefined') {
                    return {
                        currentIndex: storeListLength - 1,
                        isFocusingRight: ignoreFocusSide ? undefined : false,
                        isFocusingLeft: ignoreFocusSide ? undefined : true,
                    };
                } else {
                    const previousIndex =
                        currentIndex === 0 ? storeListLength - 1 : currentIndex - 1;
                    if (ignoreFocusSide) {
                        return {
                            currentIndex: previousIndex,
                            isFocusingRight: undefined,
                            isFocusingLeft: undefined,
                        };
                    } else {
                        if (isFocusingLeft) {
                            const previousId = store.data.list[previousIndex]?.id;
                            if (previousId && canFocusPrimary(previousId)) {
                                return {
                                    currentIndex: previousIndex,
                                    isFocusingRight: true,
                                    isFocusingLeft: false,
                                };
                            } else {
                                return {
                                    currentIndex: previousIndex,
                                    isFocusingRight: false,
                                    isFocusingLeft: true,
                                };
                            }
                        } else {
                            return {
                                currentIndex,
                                isFocusingRight: false,
                                isFocusingLeft: true,
                            };
                        }
                    }
                }
            });
        },
        [canFocusPrimary, store]
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            const { currentIndex, isFocusingRight } = keyboardNavState;
            const hoveredItem =
                typeof currentIndex === 'undefined' ? undefined : store.data.list[currentIndex];

            if (hoveredItem && e.code === 'Space') {
                e.preventDefault();
                e.stopPropagation();
                if (isFocusingRight) {
                    onSetPrimaryItem(hoveredItem);
                } else {
                    onSelectItem(hoveredItem);
                }
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                e.stopPropagation();
                focusPrev({ ignoreFocusSide: true });
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                e.stopPropagation();
                focusNext({ ignoreFocusSide: true });
            } else if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                if (e.shiftKey) {
                    focusPrev({ ignoreFocusSide: false });
                } else {
                    focusNext({ ignoreFocusSide: false });
                }
            }
        },
        [keyboardNavState, store.data.list, onSetPrimaryItem, onSelectItem, focusPrev, focusNext]
    );

    useEffect(() => {
        if (typeof currentIndex !== 'undefined') {
            virtuosoRef.current?.scrollToIndex({
                index: currentIndex,
                align: 'center',
            });
        }
    }, [currentIndex]);

    useEffect(() => {
        // We type assert here because the generic `HTMLElement` does not
        // have a typed `addEventListener`
        (elementToBindTo as HTMLDivElement).addEventListener('keydown', handleKeyDown);
        return () => {
            (elementToBindTo as HTMLDivElement).removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown, elementToBindTo]);

    useEffect(() => {
        elementToBindTo.addEventListener('click', clearKeyboardNavState);
        return () => {
            elementToBindTo.removeEventListener('click', clearKeyboardNavState);
        };
    }, [clearKeyboardNavState, elementToBindTo]);

    return {
        ...keyboardNavState,
        clearKeyboardNavState,
        virtuosoRef,
    };
}
